import { h } from '../charts/svg.ts'
import { renderBars, renderCumulative, renderDiverging, renderLegend, type ChartSeries } from '../charts/chart.ts'
import { formatCompact, formatPercent, formatSigned, formatVolume, pickVolumeUnit } from '../charts/format.ts'
import { difference, net, share } from '../report/derive.ts'
import { aggregate, type Aggregation } from '../report/aggregate.ts'
import { FLOW_VARIANTS, type FlowVariant } from '../report/flow.ts'
import type { FetchedFlow } from '../report/fetchFlow.ts'
import type { Bucket, Window } from '../lib/time.ts'
import { renderDataTable } from './datatable.ts'

/** Slot per series key, so a filter that drops a series never repaints the rest. */
const SLOT: Record<string, number> = {
  ruleExpected: 1,
  globalExpected: 1,
  ruleMeasured: 2,
  ruleUnexpected: 3,
}

export interface ReportView {
  el: HTMLElement
}

function pickSeries(
  variant: FlowVariant,
  agg: Aggregation,
  field: 'buckets' | 'cumulative',
): ChartSeries[] {
  const out: ChartSeries[] = []
  for (const s of variant.series) {
    const found = agg.series.find((a) => a.key === s.key)
    if (!found) continue
    out.push({ key: s.key, label: s.label, slot: SLOT[s.key] ?? 8, values: found[field] })
  }
  return out
}

/** The headline: how much of the period's flow was not accounted for. */
function renderHeadline(agg: Aggregation): HTMLElement {
  const exp = agg.series.find((s) => s.key === 'ruleExpected')?.total ?? 0
  const unexp = agg.series.find((s) => s.key === 'ruleUnexpected')?.total ?? 0
  const meas = agg.series.find((s) => s.key === 'ruleMeasured')?.total
  const global = agg.series.find((s) => s.key === 'globalExpected')?.total
  const unit = pickVolumeUnit(Math.max(exp, unexp, meas ?? 0, global ?? 0))
  const share = exp + unexp > 0 ? (unexp / (exp + unexp)) * 100 : 0

  const tiles: HTMLElement[] = [
    h(
      'div',
      { class: 'card stat stat-hero' },
      h('div', { class: 'stat-label', text: 'Unexpected volume, this period' }),
      h('div', { class: 'stat-value tabular', text: formatVolume(unexp, unit) }),
      h('div', { class: 'stat-note', text: `${formatPercent(share)} of all flow the rules saw` }),
    ),
    h(
      'div',
      { class: 'card stat' },
      h('div', { class: 'stat-label', text: 'Expected (flow rules)' }),
      h('div', { class: 'stat-value tabular', text: formatVolume(exp, unit) }),
    ),
  ]
  if (meas !== undefined) {
    const reconcile = meas - (exp + unexp)
    const drift = meas > 0 ? Math.abs(reconcile / meas) * 100 : 0
    tiles.push(
      h(
        'div',
        { class: 'card stat' },
        h('div', { class: 'stat-label', text: 'Measured' }),
        h('div', { class: 'stat-value tabular', text: formatVolume(meas, unit) }),
        h('div', {
          class: 'stat-note',
          text: `${reconcile >= 0 ? '+' : '−'}${formatCompact(Math.abs(reconcile) * unit.scale)} ${unit.suffix} vs expected + unexpected (${formatPercent(drift)})`,
        }),
      ),
    )
  }
  if (global !== undefined) {
    tiles.push(
      h(
        'div',
        { class: 'card stat' },
        h('div', { class: 'stat-label', text: 'Expected (controller total)' }),
        h('div', { class: 'stat-value tabular', text: formatVolume(global, unit) }),
        h('div', { class: 'stat-note', text: 'Whole controller, not scoped to flow rules' }),
      ),
    )
  }
  return h('div', { class: 'stat-row' }, ...tiles)
}

function renderVariant(variant: FlowVariant, agg: Aggregation): HTMLElement {
  const bars = pickSeries(variant, agg, 'buckets')
  const cum = pickSeries(variant, agg, 'cumulative')
  if (!cum.length) {
    return h('div', { class: 'card report-card' }, h('h2', { text: variant.title }), h('div', { class: 'empty', text: 'None of this view’s attributes are collected for this controller.' }))
  }
  const unit = pickVolumeUnit(Math.max(...cum.flatMap((s) => s.values), 1))
  const hasData = cum.some((s) => (s.values[s.values.length - 1] ?? 0) > 0)

  return h(
    'div',
    { class: 'card report-card' },
    h('div', { class: 'section-head' }, h('h2', { text: variant.title }), h('p', { text: variant.blurb })),
    hasData
      ? h(
          'div',
          { class: 'chart-block' },
          h('div', { class: 'chart-title', text: `Accumulated over the period (${unit.suffix})` }),
          renderCumulative({ bucketStarts: agg.bucketStarts, bucket: agg.bucket, series: cum, unit, height: 260 }),
        )
      : h('div', { class: 'empty', text: 'No flow volume logged in this window.' }),
    hasData
      ? h(
          'div',
          { class: 'chart-block' },
          h('div', { class: 'chart-title', text: `Per ${agg.bucket} (${unit.suffix})` }),
          h('div', { class: 'chart-sub', text: 'The individual bad days the running total smooths over.' }),
          renderBars({ bucketStarts: agg.bucketStarts, bucket: agg.bucket, series: bars, unit, height: 200 }),
          renderLegend(bars),
        )
      : null,
  )
}

/**
 * The gaps the three overlays hide. Where two measures sit almost on top of
 * each other, a plot of their difference is the only honest way to see it.
 */
function renderDifferences(agg: Aggregation): HTMLElement | null {
  const blocks: HTMLElement[] = []
  const per = `per ${agg.bucket}`

  const unexpShare = share(agg, 'ruleUnexpected', ['ruleExpected', 'ruleUnexpected'])
  if (unexpShare) {
    const exp = agg.series.find((s) => s.key === 'ruleExpected')?.total ?? 0
    const unexp = agg.series.find((s) => s.key === 'ruleUnexpected')?.total ?? 0
    const overall = exp + unexp > 0 ? (unexp / (exp + unexp)) * 100 : 0
    blocks.push(
      h(
        'div',
        { class: 'chart-block' },
        h('div', { class: 'chart-title', text: `Unexpected share of all flow, ${per} (%)` }),
        h('div', { class: 'chart-sub', text: `${formatPercent(overall)} over the whole period. A tall bar is a ${agg.bucket} where a large part of what flowed was not planned — visible here even when the volume itself is small next to the expected line.` }),
        renderBars({
          bucketStarts: agg.bucketStarts,
          bucket: agg.bucket,
          series: [{ key: 'share', label: 'Unexpected share', slot: 3, values: unexpShare }],
          unit: { suffix: '%', scale: 1, decimals: 1 },
          height: 180,
        }),
      ),
    )
  }

  const globalGap = difference(agg, 'globalExpected', ['ruleExpected'])
  if (globalGap) {
    const unit = pickVolumeUnit(Math.max(...globalGap.map(Math.abs), 1))
    blocks.push(
      h(
        'div',
        { class: 'chart-block' },
        h('div', { class: 'chart-title', text: `Controller total − flow-rule expected, ${per} (${unit.suffix})` }),
        h('div', { class: 'chart-sub', text: `Net ${formatSigned(net(globalGap) * unit.scale, unit.decimals)} ${unit.suffix} over the period. Zero means the flow rules account for everything the controller expected to emit.` }),
        renderDiverging({
          bucketStarts: agg.bucketStarts,
          bucket: agg.bucket,
          series: [{ key: 'gap', label: 'Controller − rules', slot: 1, values: globalGap }],
          unit,
          height: 200,
          polarity: { above: 'controller expected more than the rules cover', below: 'rules expected more than the controller total' },
        }),
      ),
    )
  }

  const residual = difference(agg, 'ruleMeasured', ['ruleExpected', 'ruleUnexpected'])
  if (residual) {
    const unit = pickVolumeUnit(Math.max(...residual.map(Math.abs), 1))
    blocks.push(
      h(
        'div',
        { class: 'chart-block' },
        h('div', { class: 'chart-title', text: `Reconciliation residual: measured − (expected + unexpected), ${per} (${unit.suffix})` }),
        h('div', { class: 'chart-sub', text: `Net ${formatSigned(net(residual) * unit.scale, unit.decimals)} ${unit.suffix} over the period. Zero means the sensors saw exactly what the rules expected plus what they flagged as unexpected.` }),
        renderDiverging({
          bucketStarts: agg.bucketStarts,
          bucket: agg.bucket,
          series: [{ key: 'residual', label: 'Residual', slot: 1, values: residual }],
          unit,
          height: 200,
          polarity: { above: 'sensors measured more than the rules account for', below: 'sensors measured less than the rules account for' },
        }),
      ),
    )
  }

  if (!blocks.length) return null
  return h(
    'div',
    { class: 'card report-card' },
    h(
      'div',
      { class: 'section-head' },
      h('h2', { text: 'Differences' }),
      h('p', { text: 'What the overlays above hide: the gaps between the series, drawn on their own scale.' }),
    ),
    ...blocks,
  )
}

export function renderFlowReport(
  fetched: FetchedFlow,
  window: Window,
  bucket: Bucket,
  devMode: boolean,
): HTMLElement {
  const sources = Object.values(fetched.sources).filter((s) => s !== undefined)
  const agg = aggregate(sources, window, bucket)

  const notes = fetched.notes.length
    ? h(
        'div',
        { class: 'banner banner-warning' },
        h('span', { class: 'banner-icon', text: 'i' }),
        h('div', {}, ...fetched.notes.map((n) => h('div', { text: n }))),
      )
    : null

  const available = new Set(agg.series.map((s) => s.key))
  const variants = FLOW_VARIANTS.filter((v) => v.series.some((s) => available.has(s.key)))

  const dev = devMode
    ? h(
        'div',
        { class: 'card devpanel' },
        h(
          'div',
          { class: 'section-head' },
          h('h2', { text: 'Source data' }),
          h('p', { text: 'Every row the charts above were built from, exactly as log_search returned it.' }),
        ),
        h(
          'div',
          { class: 'stat-row' },
          ...agg.series.map((s) =>
            h(
              'div',
              { class: 'card stat' },
              h('div', { class: 'stat-label', text: s.label }),
              h('div', { class: 'stat-value tabular', style: 'font-size:18px', text: `${s.samples.toLocaleString('en-GB')} rows used` }),
              h('div', {
                class: 'stat-note',
                text: `${s.dropped.toLocaleString('en-GB')} dropped · from the ${s.sourceKey} fetch`,
              }),
            ),
          ),
        ),
        ...fetched.tables.map((t) =>
          renderDataTable(
            t.attr.desc,
            `attribute ${t.attr.searchId} (${t.attr.name}) · period ${t.period ?? 'none'} · tree id ${t.attr.treeId}`,
            t.table,
          ),
        ),
      )
    : null

  return h(
    'div',
    { style: 'display:grid;gap:20px' },
    notes,
    renderHeadline(agg),
    ...variants.map((v) => renderVariant(v, agg)),
    renderDifferences(agg),
    dev,
  )
}
